import React from 'react';
import CheckboxDropdown from 'modules/common/components/checkboxDropdown';
import AuthoringFilter from 'modules/common/components/filters/authoringFilter';
import AuthoringSearch from 'modules/common/components/filters/authoringSearch';
import FilterContainer from 'modules/common/components/filters/filterContainer';
import { ShortcutState, ShortcutsFilterv1, allShortcutStates, defaultShortcutsFilter } from '../models';
import { Chip } from '@mui/material';
import CloseIcon from "@mui/icons-material/Close";
import { IdNamePair } from 'modules/common/models';
import AudienceSearchCallout from 'modules/common/components/audienceSearchCallout';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

interface IShortcutsFitlerProps {
    availableAudienceIdNames: IdNamePair<string, string>[];
    onFilterChange: (filter: ShortcutsFilterv1) => void;
    shortcutsFilter: ShortcutsFilterv1;
    hasFiltersApplied: boolean;
}

export const ShortcutsFilter: React.FC<IShortcutsFitlerProps> = ({
    availableAudienceIdNames,
    onFilterChange,
    shortcutsFilter,
    hasFiltersApplied
}) => {
    const [textSearchValue, setTextSearchValue] = React.useState<string>(defaultShortcutsFilter.searchText);

    const onApplyInternalFilters = (newFilter: ShortcutsFilterv1) => {
        onFilterChange(newFilter);
    }

    const onStateFilterChange = (newState: IdNamePair<string, ShortcutState>[]) => {
        const newFilter = {
            ...shortcutsFilter,
            state: newState.map(s => s.name)
        };

        onApplyInternalFilters(newFilter);
    }

    const onRemoveStateFromFilter = (stateToRemove: ShortcutState) => {
        const newState = shortcutsFilter.state.filter(s => s !== stateToRemove);

        const newFilter = {
            ...shortcutsFilter,
            state: newState
        };

        onApplyInternalFilters(newFilter);
    }

    const onAudienceFilterChange = (newAudiences: IdNamePair<string, string>[]) => {
        const newFilter = {
            ...shortcutsFilter, 
            audiences: newAudiences.map(a => a.id)
        };

        onApplyInternalFilters(newFilter);
    }

    const onRemoveAudienceFromFilter = (audience: string) => {
        const newAudiences = shortcutsFilter.audiences.filter(a => a !== audience);

        const newFilter = {
            ...shortcutsFilter,
            audiences: newAudiences
        };

        onApplyInternalFilters(newFilter);
    }

    const onTextFilterChange = () => {
        const newFilter = {
            ...shortcutsFilter,
            searchText: textSearchValue
        };

        onApplyInternalFilters(newFilter);
    }

    const onClearFilters = () => {
        setTextSearchValue(defaultShortcutsFilter.searchText);
        onApplyInternalFilters(defaultShortcutsFilter);
    }

    const onDeleteSearchText = () => {
        setTextSearchValue(defaultShortcutsFilter.searchText);

        const newFilter = {
            ...shortcutsFilter,
            searchText: defaultShortcutsFilter.searchText
        };

        onApplyInternalFilters(newFilter);
    }
    
    const getFilterSelection = (): JSX.Element | undefined => {
        if (!hasFiltersApplied)
            return undefined;

        return <React.Fragment>
            {shortcutsFilter.searchText !== defaultShortcutsFilter.searchText &&
                <Chip
                    key="search-text"
                    label={`"${shortcutsFilter.searchText}"`}
                    onDelete={onDeleteSearchText}
                    deleteIcon={<CloseIcon />}
                />
            }
            {shortcutsFilter.state?.map((state) =>
                <Chip
                    key={state}
                    label={state}
                    onDelete={() => onRemoveStateFromFilter(state)}
                    deleteIcon={<CloseIcon />}
                />
            )}
            {shortcutsFilter.audiences?.map((audience) =>
                <Chip
                    key={audience}
                    label={availableAudienceIdNames.find(a => a.id === audience)?.name || audience}
                    onDelete={() => onRemoveAudienceFromFilter(audience)}
                    deleteIcon={<CloseIcon />}
                />
            )}
        </React.Fragment>;
    };

    return <FilterContainer
        filterSelection={getFilterSelection()}
        filterCommands={
            <AuthoringSearch
                canSearch={hasFiltersApplied || textSearchValue !== defaultShortcutsFilter.searchText}
                onApplyFilters={onTextFilterChange}
                onClearFilters={onClearFilters}
            />
        }
        filters={
            <AuthoringFilter
                textToSearch={textSearchValue}
                searchTextPlaceholder="Search shortcut group or shortcut"
                additionalFilters={
                    <React.Fragment>
                        <CheckboxDropdown
                            label="State"
                            options={allShortcutStates.map(s => ({ name: s, id: s }))}
                            onClickOption={onStateFilterChange}
                            selectedOptions={shortcutsFilter.state.map(s => ({ name: s, id: s }))}
                        />
                        <AudienceSearchCallout
                            availableAudienceIdNames={availableAudienceIdNames}
                            selectedOptions={shortcutsFilter.audiences.map(aId => ({
                                id: aId,
                                name: availableAudienceIdNames.find(val => val.id === aId)?.name ?? ""
                            }))}
                            onAudienceFilterChange={onAudienceFilterChange}
                            buttonStyle={{
                                buttonText: "Audiences",
                                buttonVariant:"text",
                                endIcon: <ExpandMoreIcon />,
                            }}
                        />
                    </React.Fragment>
                }
                onChangeTextToSearch={onTextFilterChange}
                onUpdateTextToSearch={(e) => setTextSearchValue(e.target.value)}
            />
        }
    />;
};